/**
 * The scrapbook: every photo a pair has sent each other by post, gathered from
 * their letters and pinned up as Polaroids. Pure helpers — what belongs in it,
 * how each print sits, and how the pages are grouped — kept free of React so
 * they can be tested.
 */

import { isVisibleToRecipient } from '@toiyp/shared';
import type { ApiLetter } from '../letters/api';

export type LetterPhoto = ApiLetter['media'][number];

export interface ScrapbookPhoto {
  photo: LetterPhoto;
  letterId: string;
  /** I wrote the letter it came with. */
  mine: boolean;
  /** When it reached the reader (or, for a letter still on its way, when it was sealed). */
  at: string | null;
}

/** The frame a print comes in, from the photo's own shape. */
export type PrintShape = 'portrait' | 'square' | 'landscape';

export function printShape(width: number | null, height: number | null): PrintShape {
  if (!width || !height) return 'portrait';
  const r = width / height;
  if (r > 1.15) return 'landscape';
  if (r < 0.87) return 'portrait';
  return 'square';
}

/** A small, steady tilt for a print — the same for a photo every time it is shown. */
export function tiltFor(id: string, max = 5): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // −max…+max, never quite straight: a print pinned dead level looks placed, not dropped.
  const t = Math.round((((h >>> 0) % 2001) / 1000 - 1) * max * 10) / 10;
  const least = max / 5;
  return Math.abs(t) < least ? (t < 0 ? -least : least) : t;
}

/**
 * The photos a reader may see, newest first: everything on letters I wrote,
 * and on theirs only once the letter has arrived — the server holds the
 * picture back until then too.
 */
export function scrapbookPhotos(letters: ApiLetter[], myId: string | undefined): ScrapbookPhoto[] {
  const out: ScrapbookPhoto[] = [];
  for (const l of letters) {
    const mine = Boolean(myId) && l.authorId === myId;
    if (!mine && (l.peek || !isVisibleToRecipient(l.state))) continue;
    if (l.state === 'cancelled') continue;
    for (const photo of l.media ?? []) {
      // A photo the sender took back leaves the scrapbook; the letter still says it was there.
      if (photo.takenBack) continue;
      out.push({ photo, letterId: l.id, mine, at: l.deliveredAt ?? l.sealedAt });
    }
  }
  return out.sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));
}

export interface ScrapbookPage {
  key: string;
  /** "October 2026" */
  label: string;
  photos: ScrapbookPhoto[];
}

/** One page per month, newest first, in the order `scrapbookPhotos` gives. */
export function scrapbookPages(photos: ScrapbookPhoto[]): ScrapbookPage[] {
  const pages: ScrapbookPage[] = [];
  for (const p of photos) {
    const d = p.at ? new Date(p.at) : null;
    const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'undated';
    let page = pages.find((x) => x.key === key);
    if (!page) {
      page = {
        key,
        label: d ? d.toLocaleDateString([], { month: 'long', year: 'numeric' }) : 'Undated',
        photos: [],
      };
      pages.push(page);
    }
    page.photos.push(p);
  }
  return pages;
}

/** The photos on a letter that are still there to see. */
export function keptPhotos(media: LetterPhoto[] | undefined): LetterPhoto[] {
  return (media ?? []).filter((m) => !m.takenBack);
}

/** "1 photo", "3 photos". */
export function photoCount(n: number): string {
  return `${n} photo${n === 1 ? '' : 's'}`;
}

/** The line written under a print: who sent it, and the day. */
export function printCaption(p: Pick<ScrapbookPhoto, 'mine' | 'at'>, peerName: string): string {
  const who = p.mine ? 'from me' : `from @${peerName}`;
  if (!p.at) return who;
  return `${new Date(p.at).toLocaleDateString([], { day: 'numeric', month: 'short' })} · ${who}`;
}
